import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  GetRequestConfig,
  PostRequestConfig,
} from '../models/request-config.model';
import { Observable, tap } from 'rxjs';
import { getEncDecBody } from '../helpers/http-helpers';
import { environment } from '../../environments/environment';
import { LoaderService } from './loader.service';

interface HeaderInterface {
  key: string,
  value: string
}
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private _mySchemeBaseUrl: string = environment.mySchemeBaseUrl;
  private _nssoUrl:string = environment.nssoUrl;

  httpOptions = {
    headers: new HttpHeaders(),
    params: new HttpParams(),
    observe: null
  };
  private readonly _exploreSchemesUrl: string = environment.exploreSchemeBaseUrl;
  private readonly  _mySchemeRecommendedUrl: string = environment.mySchemeRecommendedUrl
  private readonly  userActivityUrl: string = environment.userActivityUrl;
  constructor(
    private readonly  httpClient: HttpClient,
    private readonly  loaderService: LoaderService) {}

  /**
   * Method to send a GET request.
   * @param getRequestConfig - Request config for sending a GET request.
   * @param getRequestConfig.url - URL of the GET request.
   * @param [getRequestConfig.params] - [Optional] Params of the GET request.
   * @param [getRequestConfig.headers] - [Optional] Headers of the GET request.
   * @returns Observable, which handles the GET Request.
   */
  sendGetRequest<R>({ url, params, headers, responseType }: GetRequestConfig) {
    return this.httpClient.get<R>(url, { params, headers, observe: 'body', responseType:<any>(responseType || 'json') });
  }

  /**
   * Method to send a POST request.
   * @param postRequestConfig - Request config for sending a POST request.
   * @param postRequestConfig.url - URL of the POST request.
   * @param [getRequestConfig.params] - [Optional] Params of the POST request.
   * @param [postRequestConfig.headers] - [Optional] Headers of the POST request.
   * @param postRequestConfig.body - Body of the POST request.
   * @returns Observable, which handles the POST Request.
   */
  sendPostRequest<R, B>({ url, params, headers, body }: PostRequestConfig<B>) {
    return this.httpClient.post<R>(url, body, {
      params,
      headers,
      observe: 'body',
    });
  }

  encDecPostRequest(url:any, payload:any, changeUrl?:any) {
    return new Observable(observer => {
      const reqBody = getEncDecBody(payload, changeUrl);
      this.httpClient.post(environment.frontApiUrl + 'xcommonencryptrequest/' + url, { payload: reqBody }).subscribe((res: any) => {
      try {
          observer.next(res.data || res.body.data);
        } catch (err) {
          observer.next(false);
        }
      }, (err) => {
        observer.next(false);
      })

    })
  }
  
  mySchemeGetRequest(url: String, params?: HttpParams, headerArr?: Array<HeaderInterface>, customURL?: string, type?: String): Observable<any> {
    this.loaderService.showLoader(url);
    let headers = this.getMySchemeJsonHeaders(headerArr, type);
    const options = {
      headers: headers,
      params: params,
      observe: undefined
    };
    return this.httpClient.get(customURL ? customURL : `${this._mySchemeBaseUrl}/${url}`, options).pipe(tap(res => {
      this.loaderService.hideLoader(url);
    }, err => {
      this.loaderService.hideLoader(url);
    }));
  }
  
  getMySchemeJsonHeaders(data?: any, type?: any) {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    headers = headers.set('Accept', 'application/json'); 
    if(environment.isProductionMyScheme && type !== 'eligibility'){
      headers = headers.set('X-APISETU-CLIENTID', 'in.gov.umang');
      headers = headers.set('X-APISETU-APIKEY', 'tpa7qVjMOeW1yGJbKBB0mUceMiD77NqR');
    }
    else{
      headers = headers.set('x-api-key', "jU96DEmfCG8Zir9jjsiuk9GKyFb7X22l6fAI8X5J");
    }
    return headers;
  }
  
  getExploreSchemesRequest(url: String, params?: HttpParams, headerArr?: Array<HeaderInterface>, customURL?: string): Observable<any> {
    this.loaderService.showLoader(url);
    let headers = this.getMySchemeJsonHeaders(headerArr);
    const options = {
      headers: headers,
      params: params,
      observe: undefined
    }
    return this.httpClient.get(customURL ? customURL : `${this._exploreSchemesUrl}/${url}`, options).pipe(tap(res => {
      this.loaderService.hideLoader(url);
    }, err => {
      this.loaderService.hideLoader(url);
    }));
  }
  
  mySchemeRecommendedGetRequest(url: String, params?: HttpParams, headerArr?: Array<HeaderInterface>, customURL?: string): Observable<any> {
    this.loaderService.showLoader(url);
    let headers = this.getMySchemeJsonRecommendedHeaders(headerArr);
    const options = {
      headers: headers,
      params: params,
      observe: undefined
    }
    return this.httpClient.get(customURL ? customURL : `${this._mySchemeRecommendedUrl}/${url}`, options).pipe(tap(res => {
      this.loaderService.hideLoader(url);
    }, err => {
      this.loaderService.hideLoader(url);
    }));
  }

  /**
   * Headers for MyScheme APIs
   */
   getMySchemeJsonRecommendedHeaders(data?:any, type?:any) {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    headers = headers.set('Accept', 'application/json'); 
    if(environment.isProductionMyScheme){
      headers = headers.set('x-api-key', "2qYCUKTEFJ6jSvZVcHv5ZxcauMUMhbO33MoTw940");
    } else {
     // headers = headers.set('x-api-key', "2qYCUKTEFJ6jSvZVcHv5ZxcauMUMhbO33MoTw940");
      headers = headers.set('x-api-key', "1TV3cCz12k9XGXwddWbHk7KRwOGUQUbdaCfSvTyl");
    }
    return headers;
  }
  
  callNssoGetApi(url: String, params?: HttpParams, headerArr?: Array<HeaderInterface>, customURL?: string): Observable<any>{
    this.loaderService.showLoader(url);
    let headers = this.getNssoHeaders(url);
    const options = {
      headers: headers,
      params: params,
      observe: undefined
    }
    return this.httpClient.get(customURL ? customURL : `${this._nssoUrl}/${url}`, options).pipe(tap(res => {
      this.loaderService.hideLoader(url);
    }, err => {
      this.loaderService.hideLoader(url);
    }));
  }
  
  getNssoHeaders(url?:any){
    
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    headers = headers.set('Accept', 'application/json'); 
    headers = headers.set('x-api-key', environment.nssoToken);
    const obj = {
      mode:'web',
      umang_id:"",
      old_user_id:'',
      platform:"domestic",
      token:'' ,
      lang:'',
      ip_address:"",
      device_id:""
    }

    const umangData = btoa(JSON.stringify(obj));
    headers = headers.set('data', umangData);
   
    return headers;
  }

  updateUserActivity(url: string, dataToSendInHeaders?: Object, headerArr?: Array<HeaderInterface>, customURL?: string): Observable<any> {
    this.loaderService.showLoader(url);
    
    const headers = this.getVisitedSchemeJsonHeaders(headerArr);
    const customHeaders = headers.append('data', JSON.stringify(dataToSendInHeaders));

    const options:any = {
      headers: customHeaders,
      params: null,
      observe: undefined
    };
    return this.httpClient.post(customURL ? customURL : `${this.userActivityUrl}/${url}`, null, options).pipe(
      tap(
        (res: any) => {
          this.loaderService.hideLoader(url);
        },
        (err: any) => {
          this.loaderService.hideLoader(url);
        }
      )
    );
  }

  getVisitedSchemeJsonHeaders(headerArr?: Array<HeaderInterface>): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    if(environment.isProductionMyScheme){
      headers = headers.set('x-api-key', '2qYCUKTEFJ6jSvZVcHv5ZxcauMUMhbO33MoTw940');
    }
    else{
      headers = headers.set('x-api-key', "1TV3cCz12k9XGXwddWbHk7KRwOGUQUbdaCfSvTyl");
    }
    
    return headers;
  }
}
